import React from 'react';
import { Button, Popconfirm, Spin } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { IConversation } from '../../../services/aiChat/aiChat.service';

const relativeTime = (dateStr: string): string => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const getDateGroup = (dateStr: string): string => {
  const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days <= 7) return 'This Week';
  if (days <= 30) return 'This Month';
  return 'Older';
};

const GROUP_ORDER = ['Today', 'Yesterday', 'This Week', 'This Month', 'Older'];

interface ConversationSidebarProps {
  conversations: IConversation[];
  activeConversationId: number | null;
  loading: boolean;
  hasMore: boolean;
  onSelect: (conversation: IConversation) => void;
  onNewChat: () => void;
  onDelete: (id: number) => void;
  onLoadMore: () => void;
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  conversations,
  activeConversationId,
  loading,
  hasMore,
  onSelect,
  onNewChat,
  onDelete,
  onLoadMore,
}) => {
  const list = Array.isArray(conversations) ? conversations : [];
  const grouped = GROUP_ORDER.reduce<Record<string, IConversation[]>>((acc, g) => {
    acc[g] = list.filter((c) => getDateGroup(c.updated_date) === g);
    return acc;
  }, {});

  return (
    <div className="ai-chat-sidebar">
      <div className="ai-chat-sidebar-header">
        <Button
          icon={<PlusOutlined />}
          block
          onClick={onNewChat}
          className="ai-chat-new-btn"
        >
          New Chat
        </Button>
      </div>

      <div className="ai-chat-sidebar-list">
        {loading && list.length === 0 ? (
          <div className="ai-chat-sidebar-spinner">
            <Spin size="small" />
          </div>
        ) : (
          <>
            {GROUP_ORDER.filter((g) => grouped[g].length > 0).map((group) => (
              <div key={group}>
                <div className="ai-chat-date-group">{group}</div>
                {grouped[group].map((conv) => (
                  <div
                    key={conv.id}
                    className={`ai-chat-conv-item${conv.id === activeConversationId ? ' ai-chat-conv-item--active' : ''}`}
                    onClick={() => onSelect(conv)}
                  >
                    <div className="ai-chat-conv-text">
                      <div className="ai-chat-conv-title">{conv.title}</div>
                      <div className="ai-chat-conv-time">{relativeTime(conv.updated_date)}</div>
                    </div>
                    <Popconfirm
                      title="Delete this conversation?"
                      onConfirm={(e) => { e?.stopPropagation(); onDelete(conv.id); }}
                      onCancel={(e) => e?.stopPropagation()}
                      okText="Delete"
                      okType="danger"
                    >
                      <button
                        className="ai-chat-delete-btn"
                        onClick={(e) => e.stopPropagation()}
                        title="Delete"
                      >
                        <DeleteOutlined />
                      </button>
                    </Popconfirm>
                  </div>
                ))}
              </div>
            ))}

            {hasMore && (
              <div className="ai-chat-load-more">
                <Button type="link" size="small" onClick={onLoadMore} loading={loading}>
                  Load more
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ConversationSidebar;
